import { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { AlertTriangle, RefreshCw } from "lucide-react"

import { Button } from "@/components/ui/button"
import { DataTable, type DataTableColumn } from "@/components/ui/data-table"
import { PageBody, PageHeader, PageShell, Section } from "@/components/ui/page-shell"
import { StatCard } from "@/components/ui/stat-card"
import { useToast } from "@/hooks/use-toast"
import { AnomalyAPI, type Anomaly, type AnomalySeverity } from "@/services/anomalies"

const SEVERITY_LABEL: Record<AnomalySeverity, string> = {
  error: "Error",
  warning: "Warning",
  info: "Info",
}

const KIND_LABEL: Record<Anomaly["kind"], string> = {
  orphan_invoice: "No timesheets or costs",
  drifting_timesheet: "Drifting timesheet",
  subtotal_mismatch: "Subtotal mismatch",
  period_out_of_range: "Period out of range",
}

function severityClass(s: AnomalySeverity) {
  if (s === "error") return "bg-rose-100 text-rose-800 dark:bg-rose-950/50 dark:text-rose-300"
  if (s === "warning") return "bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300"
  return "bg-sky-100 text-sky-800 dark:bg-sky-950/50 dark:text-sky-300"
}

export function InvoiceAnomalies() {
  const { toast } = useToast()
  const [loading, setLoading] = useState(true)
  const [rows, setRows] = useState<Anomaly[]>([])
  const [bySeverity, setBySeverity] = useState({ error: 0, warning: 0, info: 0 })
  const [busyIds, setBusyIds] = useState<Set<number>>(new Set())

  async function load() {
    setLoading(true)
    try {
      const data = await AnomalyAPI.list()
      setRows(data.anomalies)
      setBySeverity({
        error: data.by_severity.error ?? 0,
        warning: data.by_severity.warning ?? 0,
        info: data.by_severity.info ?? 0,
      })
    } catch (e) {
      toast({
        title: "Could not load anomalies",
        description: (e as Error).message ?? "Try again in a moment.",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function handleResetTimesheet(tsId: number) {
    setBusyIds((prev) => new Set(prev).add(tsId))
    try {
      await AnomalyAPI.resetDriftingTimesheet(tsId)
      toast({ title: "Timesheet reset", description: `Timesheet #${tsId} is back in the pool.` })
      load()
    } catch (e) {
      toast({
        title: "Reset failed",
        description: (e as Error).message ?? "Check the logs.",
        variant: "destructive",
      })
    } finally {
      setBusyIds((prev) => {
        const next = new Set(prev)
        next.delete(tsId)
        return next
      })
    }
  }

  const columns: DataTableColumn<Anomaly>[] = useMemo(
    () => [
      {
        key: "severity",
        header: "Severity",
        renderCell: (row) => (
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${severityClass(row.severity)}`}
          >
            {SEVERITY_LABEL[row.severity]}
          </span>
        ),
        badge: true,
      },
      {
        key: "kind",
        header: "Kind",
        renderCell: (row) => KIND_LABEL[row.kind] ?? row.kind,
        primary: true,
      },
      {
        key: "invoice",
        header: "Invoice",
        renderCell: (row) =>
          row.invoice_id ? (
            <Link to={`/invoices?open=${row.invoice_id}`} className="text-primary underline">
              #{row.invoice_number}
            </Link>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        key: "message",
        header: "Details",
        renderCell: (row) => (
          <span className="block max-w-xl text-muted-foreground">{row.message}</span>
        ),
        className: "min-w-0",
      },
      {
        key: "action",
        header: "",
        align: "right",
        renderCell: (row) => {
          if (row.kind !== "drifting_timesheet") return null
          const tsId = Number(row.context?.timesheet_id)
          if (!tsId) return null
          return (
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation()
                handleResetTimesheet(tsId)
              }}
              disabled={busyIds.has(tsId)}
            >
              {busyIds.has(tsId) ? "Resetting…" : "Reset"}
            </Button>
          )
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [busyIds],
  )

  return (
    <PageShell>
      <PageHeader
        title="Invoice anomalies"
        description="Problems that need human review. Stored totals remain authoritative — these are data-quality flags."
        actions={
          <Button variant="outline" onClick={load} className="gap-2">
            <RefreshCw className="h-4 w-4" /> Refresh
          </Button>
        }
      />
      <PageBody>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <StatCard
            label="Errors"
            value={bySeverity.error}
            hint="Block reconciliation"
            icon={<AlertTriangle className="h-4 w-4" />}
          />
          <StatCard label="Warnings" value={bySeverity.warning} hint="Worth a look" />
          <StatCard label="Info" value={bySeverity.info} hint="FYI only" />
        </div>

        <Section title="All findings">
          <DataTable
            columns={columns}
            rows={rows}
            rowKey={(r, i) => `${r.kind}-${r.invoice_id ?? r.context?.timesheet_id ?? i}`}
            loading={loading}
            empty="No anomalies detected."
          />
        </Section>
      </PageBody>
    </PageShell>
  )
}
